import { convertDate24Hrs } from 'helpers/formatDate';
import { amtStr } from 'helpers/numFormatter';
import {
	OrderItemDiscountStatus,
	OrderDiscountStatus,
	PaymentClass,
} from 'generated/graphql';
import BigNumber from 'bignumber.js';
import { toProperCase } from 'helpers/hooks/stringNumberFunction/stringConverter';
const dateFormat = require('dateformat');

// Function to send text to the printer with Font A (monospaced) and size 1, with optional bold (0 or 1)
export const printText = (printer, text) => {
	const outEndpoint = printer.configuration.interfaces[0]?.alternates[0]?.endpoints.find(
		ep => ep.direction === 'out',
	);

	if (!outEndpoint) {
		console.error('OUT endpoint not found!');
		alert('Failed to find OUT endpoint');
		return;
	}

	console.log('OUT endpoint found:', outEndpoint);
	console.log('Sending data to printer:', text);

	// ESC/POS command for selecting Font A (monospaced)
	const FONT_A = new Uint8Array([0x1b, 0x74, 0x00]); // ESC t 0x00 selects Font A (monospaced)

	// ESC/POS command for setting font size to 1 (normal size)
	const SIZE_1 = new Uint8Array([0x1b, 0x21, 0x00]); // ESC ! 0x00 sets font size to 1

	const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

	try {
		// Select Font A (monospaced)
		printer.transferOut(outEndpoint.endpointNumber, FONT_A);

		// Set font size to 1 (normal size)
		printer.transferOut(outEndpoint.endpointNumber, SIZE_1);

		// Send the actual text to the printer
		printer.transferOut(outEndpoint.endpointNumber, text);

		console.log('Data sent to printer successfully.');
	} catch (error) {
		console.error('Error while sending data to the printer:', error);
		alert('Failed to send data to the printer!');
	}
};

// Function to send text to the printer with Font A (monospaced) and size 1, with optional bold (0 or 1)
export const printTextBold = (printer, text) => {
	const outEndpoint = printer.configuration.interfaces[0]?.alternates[0]?.endpoints.find(
		ep => ep.direction === 'out',
	);

	if (!outEndpoint) {
		console.error('OUT endpoint not found!');
		alert('Failed to find OUT endpoint');
		return;
	}

	console.log('OUT endpoint found:', outEndpoint);
	console.log('Sending data to printer:', text);

	// ESC/POS command for selecting Font A (monospaced)
	const FONT_A = new Uint8Array([0x1b, 0x74, 0x00]); // ESC t 0x00 selects Font A (monospaced)

	// ESC/POS command for setting font size to 1 (normal size)
	const SIZE_1 = new Uint8Array([0x1b, 0x21, 0x00]); // ESC ! 0x00 sets font size to 1

	// ESC/POS command for enabling bold text
	const BOLD_ON = new Uint8Array([0x1b, 0x45, 0x01]); // ESC E 0x01 enables bold

	// ESC/POS command for disabling bold text
	const BOLD_OFF = new Uint8Array([0x1b, 0x45, 0x00]); // ESC E 0x00 disables bold

	try {
		// Select Font A (monospaced)
		printer.transferOut(outEndpoint.endpointNumber, FONT_A);

		// Set font size to 1 (normal size)
		printer.transferOut(outEndpoint.endpointNumber, SIZE_1);

		// Apply bold text if isBold is 1
		printer.transferOut(outEndpoint.endpointNumber, BOLD_ON);

		// Send the actual text to the printer
		printer.transferOut(outEndpoint.endpointNumber, text);

		console.log('Data sent to printer successfully.');
	} catch (error) {
		console.error('Error while sending data to the printer:', error);
		alert('Failed to send data to the printer!');
	}
};

export const printTextBoldBorder = (printer, rawText) => {
	const outEndpoint = printer.configuration.interfaces[0]?.alternates[0]?.endpoints.find(
		ep => ep.direction === 'out',
	);

	if (!outEndpoint) {
		console.error('OUT endpoint not found!');
		alert('Failed to find OUT endpoint');
		return;
	}

	console.log('OUT endpoint found:', outEndpoint);

	const FONT_A = new Uint8Array([0x1b, 0x74, 0x00]); // ESC t 0x00: Font A
	const SIZE_1 = new Uint8Array([0x1b, 0x21, 0x00]); // ESC ! 0x00: normal size
	const BOLD_ON = new Uint8Array([0x1b, 0x45, 0x01]); // ESC E 0x01: bold on
	const BOLD_OFF = new Uint8Array([0x1b, 0x45, 0x00]); // ESC E 0x00: bold off

	const BOX_WIDTH = 32;

	// Pads text with spaces to center it in the box
	const centerText = (text, width = BOX_WIDTH) => {
		const padding = Math.floor((width - text.length) / 2);
		return (
			' '.repeat(padding) + text + ' '.repeat(width - padding - text.length)
		);
	};

	// Use box-drawing characters. Fallback to +-| if necessary
	// Use ASCII characters for compatibility
	const topBorder = '+' + '-'.repeat(BOX_WIDTH) + '+\n';
	const middleLine = '|' + centerText(rawText) + '|\n';
	const bottomBorder = '+' + '-'.repeat(BOX_WIDTH) + '+\n';

	const fullText = topBorder + middleLine + bottomBorder;
	const encodedText = new TextEncoder().encode(fullText);

	try {
		// Apply styles
		printer.transferOut(outEndpoint.endpointNumber, FONT_A);
		printer.transferOut(outEndpoint.endpointNumber, SIZE_1);
		printer.transferOut(outEndpoint.endpointNumber, BOLD_ON);

		// Send formatted text
		printer.transferOut(outEndpoint.endpointNumber, encodedText);

		// Turn off bold after printing
		printer.transferOut(outEndpoint.endpointNumber, BOLD_OFF);

		console.log('Bold boxed text sent to printer.');
	} catch (error) {
		console.error('Error while sending data to the printer:', error);
		alert('Failed to send data to the printer!');
	}
};

function createReceiptLineLeftMiddleRight(
	leftText,
	middleText,
	rightText,
	printer,
	LINE_CHARS,
	isBold,
	newline,
	underline, // 0–4: which parts to underline
	side, // 0–2: where to place underline(s)
	leftColRatio = 0.6, // New optional parameter
	middleColRatio = 0.1, // New optional parameter
) {
	const printMethod = isBold === 1 ? printTextBold : printText;

	const leftColWidth = Math.floor(LINE_CHARS * leftColRatio);
	const middleColWidth = Math.floor(LINE_CHARS * middleColRatio);
	const rightColWidth = LINE_CHARS - leftColWidth - middleColWidth;

	function wrapWords(text, width) {
		const result = [];
		const words = text.split(' ');
		let line = '';

		for (let word of words) {
			while (word.length > width) {
				if (line) {
					result.push(line);
					line = '';
				}
				result.push(word.slice(0, width));
				word = word.slice(width);
			}
			if ((line + ' ' + word).trim().length <= width) {
				line = (line + ' ' + word).trim();
			} else {
				if (line) result.push(line);
				line = word;
			}
		}
		if (line) result.push(line);
		return result;
	}

	const leftLines = wrapWords(leftText, leftColWidth);
	const middleLines = wrapWords(middleText, middleColWidth);
	const rightLines = wrapWords(rightText, rightColWidth);

	const maxLines = Math.max(
		leftLines.length,
		middleLines.length,
		rightLines.length,
	);

	function centerText(text, width) {
		const pad = Math.max(0, width - text.length);
		const padStart = Math.floor(pad / 2);
		const padEnd = pad - padStart;
		return ' '.repeat(padStart) + text + ' '.repeat(padEnd);
	}

	// 0 no underline
	// 1 is for left text underline
	// 2 is for middle text underline
	// 3 is for right text underline
	// 4 is for all text underline
	// 5 if for middle and right text underline

	// Create underline string generator
	function getUnderlineLine() {
		let leftUnderline = ' '.repeat(leftColWidth);
		let middleUnderline = ' '.repeat(middleColWidth);
		let rightUnderline = ' '.repeat(rightColWidth);

		if ([1, 4, 5].includes(underline)) {
			rightUnderline = '-'.repeat(rightColWidth);
		}
		if ([2, 4, 5].includes(underline)) {
			middleUnderline = '-'.repeat(middleColWidth);
		}
		if ([3, 4].includes(underline)) {
			leftUnderline = '-'.repeat(leftColWidth);
		}

		return leftUnderline + middleUnderline + rightUnderline;
	}

	// 0 mean no line
	// 1 mean bottom line
	// 2 mean top and bottom line

	// TOP underline if requested
	if (side === 2 && underline !== 0) {
		const topUnderline = getUnderlineLine();
		printMethod(printer, new TextEncoder().encode(`${topUnderline}${newline}`));
	}

	// Print all content lines
	for (let i = 0; i < maxLines; i++) {
		const left = (leftLines[i] || '').padEnd(leftColWidth);
		const middle = centerText(middleLines[i] || '', middleColWidth);
		const right = (rightLines[i] || '').padStart(rightColWidth);
		const line = left + middle + right;
		printMethod(printer, new TextEncoder().encode(`${line}${newline}`));
	}

	// BOTTOM underline if requested
	if ((side === 1 || side === 2) && underline !== 0) {
		const bottomUnderline = getUnderlineLine();
		printMethod(
			printer,
			new TextEncoder().encode(`${bottomUnderline}${newline}`),
		);
	}
}

export const printDayEndReportReceiptUSBPOS = async (
	printer,
	outlet,
	date,
	daySalesSummary,
	billDiscountCheckSummary,
	paymentTypeSummaryV2Data,
	mealPeriodSummaryData,
	dayEndReportGetDiscountSummaryData,
	paymentByUserSummaryData,
	coverSummaryData,
	coverByMealPeriodSummaryData,
	menuItemSummaryData,
	majorSummaryData,
	familySummaryData,
	billDetailSummaryData,
	voidBillSummaryData,
	billDiscountSummaryData,
	filterValue,
	deviceAlignmentCode,
) => {
	const outletPoscode =
		outlet?.address?.postCode !== null ||
		outlet?.address?.postCode !== undefined
			? outlet?.address?.postCode + ','
			: '';

	const outletCity =
		outlet?.address?.city !== null || outlet?.address?.city !== undefined
			? outlet?.address?.city + ','
			: '';

	const outletState =
		outlet?.address?.state !== null || outlet?.address?.state !== undefined
			? outlet?.address?.state
			: '';

	const formattedDate = [
		String(date.getDate()).padStart(2, '0'),
		String(date.getMonth() + 1).padStart(2, '0'), // Months are 0-based
		date.getFullYear(),
	].join('/');

	const currentDate = new Date();
	// Convert to local time components
	const day = currentDate.getDate(); // no leading zero
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = currentDate.getFullYear();

	let hours = currentDate.getHours();
	const minutes = String(currentDate.getMinutes()).padStart(2, '0');
	const ampm = hours >= 12 ? 'PM' : 'AM';

	hours = hours % 12;
	hours = hours === 0 ? 12 : hours; // Convert 0 to 12

	const formattedCurrentDate = `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;

	const LINE_CHARS = deviceAlignmentCode;

	// Initialize Printer (ESC/POS Reset command)
	const ESC_POS_INIT = new Uint8Array([0x1b, 0x40]);
	printText(printer, ESC_POS_INIT); // Reset the printer
	console.log('Printer initialized successfully.');

	// Print Store Name Center Aligned
	const ESC_POS_CENTER = new Uint8Array([0x1b, 0x61, 0x01]);

	printText(printer, ESC_POS_CENTER); // Align center
	printText(printer, new TextEncoder().encode(`${outlet?.name}\n`));
	printText(printer, new TextEncoder().encode(`${outlet?.address?.address}\n`));
	printText(
		printer,
		new TextEncoder().encode(`${outletPoscode}${outletCity}\n`),
	);
	printText(printer, new TextEncoder().encode(`${outletState}.\n\n`));
	printTextBold(printer, new TextEncoder().encode(`Day End Report\n\n`));
	createReceiptLineLeftMiddleRight(
		``,
		``,
		`Page 1 of 1`,
		printer,
		LINE_CHARS,
		1,
		`\n`,
		0,
		0,
		0.6,
		0.0,
	);
	printText(printer, ESC_POS_INIT); // Reset the printer
	printText(
		printer,
		new TextEncoder().encode(`Outlet        : ${outlet?.name}\n`),
	);
	printText(
		printer,
		new TextEncoder().encode(`Business Date : ${formattedDate}\n`),
	);
	printText(
		printer,
		new TextEncoder().encode(`Print Date    : ${formattedCurrentDate}\n\n`),
	);
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Day Sales Summary`);
	// printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_INIT); // Reset the printer
	// day sales summary
	{
		const totalCover = parseFloat(daySalesSummary[0]?.totalCover);
		const formattedCover = Number.isInteger(totalCover)
			? totalCover
			: totalCover.toFixed(2);

		createReceiptLineLeftMiddleRight(
			`Total Cover`,
			``,
			`${formattedCover}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Paid Bill-     ${daySalesSummary[0]?.billCount}`,
			``,
			`${daySalesSummary[0]?.subTotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Sales`,
			``,
			`${daySalesSummary[0]?.surcharge}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Discount`,
			``,
			`${
				daySalesSummary[0]?.discount > 0
					? '(' + daySalesSummary[0]?.discount + ')'
					: '0.00'
			}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Total`,
			``,
			`${daySalesSummary[0]?.subTotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			2,
		);
		createReceiptLineLeftMiddleRight(
			`Service Charge`,
			``,
			`${daySalesSummary[0]?.serviceCharge}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Tax`,
			``,
			`${daySalesSummary[0]?.taxAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Nett Sales`,
			``,
			`${daySalesSummary[0]?.nettSales}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Nett Discount`,
			``,
			`${
				daySalesSummary[0]?.nettDiscount > 0
					? '(' + daySalesSummary[0]?.nettDiscount + ')'
					: '0.00'
			}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			1,
		);
		createReceiptLineLeftMiddleRight(
			`Sub Total`,
			``,
			`${daySalesSummary[0]?.nettSubtotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Service Charge`,
			``,
			`${daySalesSummary[0]?.serviceCharge}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Tax`,
			``,
			`${daySalesSummary[0]?.taxAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Tips`,
			``,
			`${daySalesSummary[0]?.tipsAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Rounding`,
			``,
			`${daySalesSummary[0]?.roundingAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Total`,
			``,
			`${daySalesSummary[0]?.subTotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			2,
		);
		printText(printer, new TextEncoder().encode(`\n`));
		createReceiptLineLeftMiddleRight(
			`OC`,
			``,
			`${Number(billDiscountCheckSummary[0]?.OC).toFixed(2) ?? '0.00'}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`ENT`,
			``,
			`${Number(billDiscountCheckSummary[0]?.ENT).toFixed(2) ?? '0.00'}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Total`,
			``,
			`${Number(billDiscountCheckSummary[0]?.Total).toFixed(2) ?? '0.00'}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			2,
		);
	}
	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Payment Type Summary`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// payment type summary
	{
		let totalCount = 0;
		let totalSummary = 0;
		for (const pt of paymentTypeSummaryV2Data) {
			const value = Number(pt?.summary || 0);
			const count = Number(pt?.count || 0);
			const formatted = value.toFixed(2);

			totalCount += count;
			totalSummary += value;
			createReceiptLineLeftMiddleRight(
				`${pt?.info}`,
				`${pt?.count}`,
				`${formatted}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				2,
				0,
			);
		}
		createReceiptLineLeftMiddleRight(
			`Total`,
			`${totalCount}`,
			`${totalSummary.toFixed(2)}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			5,
			2,
		);
	}
	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Meal Summary`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// meal summary
	{
		let totalQty = 0;
		let totalSummary = 0;
		createReceiptLineLeftMiddleRight(
			`Meal Period`,
			`Qty`,
			`Sales Amount`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			4,
			1,
		);
		for (const mp of mealPeriodSummaryData) {
			const value = Number(mp?.salesAmt || 0);
			const qty = Number(mp?.qty || 0);
			const formatted = value.toFixed(2);

			totalQty += qty;
			totalSummary += value;
			createReceiptLineLeftMiddleRight(
				`${mp?.mealPeriod}`,
				`${mp?.qty}`,
				`${formatted}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				5,
				1,
			);
		}
		createReceiptLineLeftMiddleRight(
			`Sub total (Before Discount)`,
			`${totalQty}`,
			`${daySalesSummary[0]?.surcharge}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Discount`,
			``,
			`${
				dayEndReportGetDiscountSummaryData[0]?.Discount > 0
					? '(' + daySalesSummary[0]?.discount + ')'
					: '0.00'
			}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Sub total (After Discount)`,
			``,
			`${daySalesSummary[0]?.subTotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Tax`,
			``,
			`${daySalesSummary[0]?.taxAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Service Charge`,
			``,
			`${daySalesSummary[0]?.serviceCharge}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Rounding`,
			``,
			`${daySalesSummary[0]?.roundingAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Total`,
			`${totalQty}`,
			`${daySalesSummary[0]?.subTotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			5,
			2,
		);
	}
	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Payment (By User)`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// payment (by user)
	{
		let totalSummary = 0;

		createReceiptLineLeftMiddleRight(
			`PaymentType`,
			``,
			`Amount`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			4,
			1,
		);

		// Group data by billNo
		const groupedData = {};

		for (const pt of paymentByUserSummaryData) {
			if (!groupedData[pt.billNo]) {
				groupedData[pt.billNo] = [];
			}
			groupedData[pt.billNo].push(pt);
		}

		// Loop through each group
		for (const billNo in groupedData) {
			let billTotal = 0; // <-- New per-bill total

			createReceiptLineLeftMiddleRight(
				`User: ${billNo}`,
				``,
				``,
				printer,
				LINE_CHARS,
				1,
				`\n`,
				0,
				0,
			);

			for (const pt of groupedData[billNo]) {
				const value = Number(pt?.amount || 0);
				billTotal += value; // <-- Add to per-bill total
				totalSummary += value; // <-- Add to overall total

				if (value !== 0) {
					createReceiptLineLeftMiddleRight(
						`${pt?.type}`,
						``,
						`${value.toFixed(2)}`,
						printer,
						LINE_CHARS,
						0,
						`\n`,
						0,
						0,
					);
				}
			}

			createReceiptLineLeftMiddleRight(
				`Total`,
				``,
				`${billTotal.toFixed(2)}`, // <-- Use per-bill total
				printer,
				LINE_CHARS,
				0,
				`\n`,
				1,
				2,
			);
		}

		createReceiptLineLeftMiddleRight(
			`Overall Total`,
			``,
			`${totalSummary.toFixed(2)}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			2,
		);
	}
	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Cover`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// cover summary
	{
		let totalSummary = 0;
		createReceiptLineLeftMiddleRight(
			`Guest Type`,
			``,
			`No. of Pax`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			4,
			1,
		);
		for (const cs of coverSummaryData) {
			const value = Number(cs?.pax || 0);
			totalSummary += value;

			let guestType;
			if (cs?.guest === 'MEMBER') {
				guestType = 'Member';
			} else if (cs?.guest === 'STAFF') {
				guestType = 'Staff';
			} else if (cs?.guest === 'OFFICER') {
				guestType = 'Officer';
			} else if (cs?.guest === 'VISITOR') {
				guestType = 'VISITOR';
			} else if (cs?.guest === 'HOTEL_GUEST') {
				guestType = 'Hotel Guest';
			}
			createReceiptLineLeftMiddleRight(
				`${guestType}`,
				``,
				`${cs?.pax}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
			);
		}
		createReceiptLineLeftMiddleRight(
			`Total Cover`,
			``,
			`${totalSummary}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			2,
		);
	}
	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Cover By Meal period`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// cover by meal period
	{
		let totalSummary = 0;
		createReceiptLineLeftMiddleRight(
			`Meal Period`,
			`Guest Types`,
			`No. of Pax`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			4,
			1,
			0.4,
			0.3,
		);
		// Group data by billNo
		const groupedData = {};

		for (const pt of coverByMealPeriodSummaryData) {
			if (!groupedData[pt.mealperiod]) {
				groupedData[pt.mealperiod] = [];
			}
			groupedData[pt.mealperiod].push(pt);
		}

		// Loop through each group
		for (const mealperiod in groupedData) {
			let paxTotal = 0; // <-- New per-bill total

			createReceiptLineLeftMiddleRight(
				`${mealperiod}`,
				``,
				``,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
				0.4,
				0.3,
			);

			for (const pt of groupedData[mealperiod]) {
				const value = Number(pt?.pax || 0);
				paxTotal += value; // <-- Add to per-bill total
				totalSummary += value; // <-- Add to overall total

				if (value !== 0) {
					createReceiptLineLeftMiddleRight(
						``,
						`${pt?.guest}`,
						`${value}`,
						printer,
						LINE_CHARS,
						0,
						`\n`,
						0,
						0,
						0.4,
						0.3,
					);
				}
			}
		}

		createReceiptLineLeftMiddleRight(
			`Total Cover`,
			``,
			`${totalSummary}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			2,
		);
	}
	// menu item summary
	if (filterValue?.itemDetail === true) {
		printText(printer, new TextEncoder().encode(`\n`));
		printText(printer, ESC_POS_CENTER); // Align center
		printTextBoldBorder(printer, `Item Details`);
		printText(printer, ESC_POS_INIT); // Reset the printer
		{
			let totalQty = 0;
			let totalSummary = 0;
			createReceiptLineLeftMiddleRight(
				`Description`,
				`Qty`,
				`Amount`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				4,
				1,
			);
			for (const mi of menuItemSummaryData) {
				const value = Number(mi?.salesAmt || 0);
				const qty = Number(mi?.qty || 0);
				const formatted = value.toFixed(2);

				totalQty += qty;
				totalSummary += value;
				createReceiptLineLeftMiddleRight(
					`${mi?.item}`,
					`${mi?.qty}`,
					`${formatted}`,
					printer,
					LINE_CHARS,
					0,
					`\n`,
					5,
					1,
				);
			}
			createReceiptLineLeftMiddleRight(
				`Sub total (Before Discount)`,
				`${totalQty}`,
				`${daySalesSummary[0]?.surcharge}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
			);
			createReceiptLineLeftMiddleRight(
				`Discount`,
				``,
				`${
					dayEndReportGetDiscountSummaryData[0]?.Discount > 0
						? '(' + daySalesSummary[0]?.discount + ')'
						: '0.00'
				}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
			);
			createReceiptLineLeftMiddleRight(
				`Sub total (After Discount)`,
				``,
				`${daySalesSummary[0]?.subTotal}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
			);
			createReceiptLineLeftMiddleRight(
				`Tax`,
				``,
				`${daySalesSummary[0]?.taxAmt}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
			);
			createReceiptLineLeftMiddleRight(
				`Service Charge`,
				``,
				`${daySalesSummary[0]?.serviceCharge}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
			);
			createReceiptLineLeftMiddleRight(
				`Rounding`,
				``,
				`${daySalesSummary[0]?.roundingAmt}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
			);
			createReceiptLineLeftMiddleRight(
				`Total`,
				`${totalQty}`,
				`${daySalesSummary[0]?.subTotal}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				5,
				2,
			);
		}
	}

	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Major Summary`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// major summary
	{
		let totalQty = 0;
		let totalSummary = 0;
		createReceiptLineLeftMiddleRight(
			`Major Code`,
			`Qty`,
			`Amount`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			4,
			1,
		);
		for (const mi of majorSummaryData) {
			const value = Number(mi?.salesAmt || 0);
			const qty = Number(mi?.qty || 0);
			const formatted = value.toFixed(2);

			totalQty += qty;
			totalSummary += value;
			createReceiptLineLeftMiddleRight(
				`${mi?.major}`,
				`${mi?.qty}`,
				`${formatted}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				5,
				1,
			);
		}
		createReceiptLineLeftMiddleRight(
			`Sub total (Before Discount)`,
			`${totalQty}`,
			`${daySalesSummary[0]?.surcharge}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Discount`,
			``,
			`${
				dayEndReportGetDiscountSummaryData[0]?.Discount > 0
					? '(' + daySalesSummary[0]?.discount + ')'
					: '0.00'
			}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Sub total (After Discount)`,
			``,
			`${daySalesSummary[0]?.subTotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Tax`,
			``,
			`${daySalesSummary[0]?.taxAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Service Charge`,
			``,
			`${daySalesSummary[0]?.serviceCharge}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Rounding`,
			``,
			`${daySalesSummary[0]?.roundingAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Total`,
			`${totalQty}`,
			`${daySalesSummary[0]?.subTotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			5,
			2,
		);
	}

	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Family Summary`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// family summary
	{
		let totalQty = 0;
		let totalSummary = 0;
		createReceiptLineLeftMiddleRight(
			`Family`,
			`Qty`,
			`Amount`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			4,
			1,
		);
		for (const mi of familySummaryData) {
			const value = Number(mi?.salesAmt || 0);
			const qty = Number(mi?.qty || 0);
			const formatted = value.toFixed(2);

			totalQty += qty;
			totalSummary += value;
			createReceiptLineLeftMiddleRight(
				`${mi?.family}`,
				`${mi?.qty}`,
				`${formatted}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				5,
				1,
			);
		}
		createReceiptLineLeftMiddleRight(
			`Sub total (Before Discount)`,
			`${totalQty}`,
			`${daySalesSummary[0]?.surcharge}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Discount`,
			``,
			`${
				dayEndReportGetDiscountSummaryData[0]?.Discount > 0
					? '(' + daySalesSummary[0]?.discount + ')'
					: '0.00'
			}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Sub total (After Discount)`,
			``,
			`${daySalesSummary[0]?.subTotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Tax`,
			``,
			`${daySalesSummary[0]?.taxAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Service Charge`,
			``,
			`${daySalesSummary[0]?.serviceCharge}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Rounding`,
			``,
			`${daySalesSummary[0]?.roundingAmt}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			0,
			0,
		);
		createReceiptLineLeftMiddleRight(
			`Total`,
			`${totalQty}`,
			`${daySalesSummary[0]?.subTotal}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			5,
			2,
		);
	}

	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Bill Details`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// bill details summary
	{
		let totalSummary = 0;
		createReceiptLineLeftMiddleRight(
			`Bill No`,
			`Type`,
			`Amount`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			4,
			1,
			0.4,
			0.3,
		);
		for (const bd of billDetailSummaryData) {
			let label = '';
			if (bd?.type === 'CASH') {
				label = 'Cash';
			} else if (bd?.type === 'VISA_CARD') {
				label = 'Visa Card';
			} else if (bd?.type === 'MASTERCARD') {
				label = 'Master Card';
			} else if (bd?.type === 'AMERICAN_EXPRESS_CARD') {
				label = 'American Express Card';
			} else if (bd?.type === 'DINERS_CARD') {
				label = 'Diners Card';
			} else if (bd?.type === 'JCB_CARD') {
				label = 'JCB Card';
			} else if (bd?.type === 'DEBIT_CARD') {
				label = 'Debit Card';
			} else if (bd?.type === 'UNIONPAY_CARD') {
				label = 'UNION PAY';
			} else if (bd?.type === 'EWALLET') {
				label = 'E-WALLET';
			} else if (bd?.type === 'ONLINE') {
				label = 'Online';
			} else if (bd?.type === 'CREDIT') {
				label = 'Credit';
			} else if (bd?.type === 'ROOM') {
				label = 'Room';
			} else if (bd?.type === 'NON_GUEST') {
				label = 'Non Guest';
			} else if (bd?.type === 'MEMBER') {
				label = 'Member';
			} else if (bd?.type === 'SKIPPER') {
				label = 'Skipper';
			} else if (bd?.type === 'OFFICER') {
				label = 'Officer';
			} else if (bd?.type === 'STAFF') {
				label = 'Staff';
			} else if (bd?.type === 'VOUCHER') {
				label = 'Voucher';
			} else if (bd?.type === 'UNSETTLED_BILL') {
				label = 'Unsettled Bill';
			} else if (bd?.type === 'SUSPEND_FOLIO') {
				label = 'Suspend Folio';
			} else if (bd?.type === 'BANK_TT') {
				label = 'Bank TT';
			} else if (bd?.type === 'CHEQUE') {
				label = 'Cheque';
			} else if (bd?.type === 'DEBTOR') {
				label = 'Debtor';
			} else if (bd?.type === 'ENTERTAINMENT') {
				label = 'ENT';
			} else if (bd?.type === 'OFFICER_CHECK') {
				label = 'OC';
			} else {
				label = bd?.type; // fallback to input value
			}

			const value = Number(bd?.amount || 0);
			const formatted = value.toFixed(2);
			totalSummary += value;
			createReceiptLineLeftMiddleRight(
				`${bd?.billNo}`,
				`${label}`,
				`${formatted}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
				0.4,
				0.3,
			);
		}
		createReceiptLineLeftMiddleRight(
			`Total`,
			``,
			`${totalSummary.toFixed(2)}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			2,
			0.4,
			0.3,
		);
	}

	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Void Bill Details`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// void bill details summary
	{
		let totalSummary = 0;
		createReceiptLineLeftMiddleRight(
			`Bill No`,
			`Type`,
			`Amount`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			4,
			1,
			0.4,
			0.3,
		);
		for (const bd of voidBillSummaryData) {
			let label;
			if (bd?.paymenttype === 'CASH') {
				label = 'Cash';
			} else if (bd?.paymenttype === 'VISA_CARD') {
				label = 'Visa Card';
			} else if (bd?.paymenttype === 'MASTERCARD') {
				label = 'Master Card';
			} else if (bd?.paymenttype === 'AMERICAN_EXPRESS_CARD') {
				label = 'American Express Card';
			} else if (bd?.paymenttype === 'DINERS_CARD') {
				label = 'Diners Card';
			} else if (bd?.paymenttype === 'JCB_CARD') {
				label = 'JCB Card';
			} else if (bd?.paymenttype === 'DEBIT_CARD') {
				label = 'Debit Card';
			} else if (bd?.paymenttype === 'UNIONPAY_CARD') {
				label = 'UNION PAY';
			} else if (bd?.paymenttype === 'EWALLET') {
				label = 'E-WALLET';
			} else if (bd?.paymenttype === 'ONLINE') {
				label = 'Online';
			} else if (bd?.paymenttype === 'CREDIT') {
				label = 'Credit';
			} else if (bd?.paymenttype === 'ROOM') {
				label = 'Room';
			} else if (bd?.paymenttype === 'NON_GUEST') {
				label = 'Non Guest';
			} else if (bd?.paymenttype === 'MEMBER') {
				label = 'Member';
			} else if (bd?.paymenttype === 'SKIPPER') {
				label = 'Skipper';
			} else if (bd?.paymenttype === 'OFFICER') {
				label = 'Officer';
			} else if (bd?.paymenttype === 'STAFF') {
				label = 'Staff';
			} else if (bd?.paymenttype === 'VOUCHER') {
				label = 'Voucher';
			} else if (bd?.paymenttype === 'UNSETTLED_BILL') {
				label = 'Unsettled Bill';
			} else if (bd?.paymenttype === 'SUSPEND_FOLIO') {
				label = 'Suspend Folio';
			} else if (bd?.paymenttype === 'BANK_TT') {
				label = 'Bank TT';
			} else if (bd?.paymenttype === 'CHEQUE') {
				label = 'Cheque';
			} else if (bd?.paymenttype === 'DEBTOR') {
				label = 'Debtor';
			} else if (bd?.paymenttype === 'ENTERTAINMENT') {
				label = 'ENT';
			} else if (bd?.paymenttype === 'OFFICER_CHECK') {
				label = 'OC';
			} else {
				label = bd?.paymenttype; // fallback to input value
			}

			const value = Number(bd?.amount || 0);
			const formatted = value.toFixed(2);
			totalSummary += value;
			createReceiptLineLeftMiddleRight(
				`${bd?.billNo}`,
				`${label}`,
				`${formatted}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
				0.4,
				0.3,
			);
		}
		createReceiptLineLeftMiddleRight(
			`Total`,
			``,
			`${totalSummary.toFixed(2)}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			2,
			0.4,
			0.3,
		);
	}

	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, ESC_POS_CENTER); // Align center
	printTextBoldBorder(printer, `Disc Details`);
	printText(printer, ESC_POS_INIT); // Reset the printer
	// disc detail summary
	{
		let totalSummary = 0;
		createReceiptLineLeftMiddleRight(
			`Bill No`,
			`Disc Code`,
			`Amount`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			4,
			1,
			0.4,
			0.3,
		);
		for (const bd of billDiscountSummaryData) {
			const value = Number(bd?.amount || 0);
			const formatted = value.toFixed(2);
			totalSummary += value;
			createReceiptLineLeftMiddleRight(
				`${bd?.billNo}`,
				`${bd?.discCode}`,
				`${formatted}`,
				printer,
				LINE_CHARS,
				0,
				`\n`,
				0,
				0,
				0.4,
				0.3,
			);
		}
		createReceiptLineLeftMiddleRight(
			`Total`,
			``,
			`${totalSummary.toFixed(2)}`,
			printer,
			LINE_CHARS,
			0,
			`\n`,
			1,
			2,
			0.4,
			0.3,
		);
	}
	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, new TextEncoder().encode(`\n`));
	createReceiptLineLeftMiddleRight(
		``,
		`Day End Report`,
		``,
		printer,
		LINE_CHARS,
		1,
		`\n`,
		4,
		2,
		0.3,
		0.4,
	);
	printText(printer, new TextEncoder().encode(`\n`));
	createReceiptLineLeftMiddleRight(
		``,
		``,
		`Total 1 Pages`,
		printer,
		LINE_CHARS,
		1,
		`\n`,
		0,
		0,
		0.6,
		0.0,
	);

	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, new TextEncoder().encode(`\n`));
	printText(printer, new TextEncoder().encode(`\n`));
	// Only cut the paper once all print jobs are completed
	const ESC_POS_CUT = new Uint8Array([0x1d, 0x56, 0x00]);
	await printText(printer, ESC_POS_CUT); // Send paper cut command here
};
